// Script values (SCR-1, SCR-5 §27.2). PURE (ENG-1) — a leaf module with no imports.
//
// The language has exactly five value types: number, string, bool, vec3 and list.
// v1 identified a vec3 as "`typeof v === 'object'`, and vec3 is the only object
// value"; lists break that invariant, so every non-primitive value now carries a
// `__t` discriminant and `isVec3`/`isList` test the TAG, never `typeof`. Both object
// values are frozen (a list's `items` array too), which is what lets them be shared
// freely — `push` returns a new list rather than mutating one.
//
// Everything here is total and deterministic — no NaN leaks into a comparison, and
// no object identity ever affects equality: a list built twice from equal elements
// *is* the same value (§27.2, R4).

export interface Vec3 {
  readonly __t: 'vec3';
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** An immutable, ordered sequence of values (SCR-5). Length is bounded by
 *  `SCR.LIST_MAX`; the API (`list`/`push`) enforces it, aborting the dispatch on
 *  overflow (E-30). Lists may nest. */
export interface ScriptList {
  readonly __t: 'list';
  readonly items: readonly Value[];
}

export type Value = number | string | boolean | Vec3 | ScriptList;

export type ValueType = 'number' | 'string' | 'bool' | 'vec3' | 'list';

/** Construct an (immutable) vec3 value. Non-finite components collapse to 0 so a
 *  bad computation can never poison later arithmetic non-deterministically. */
export function vec3(x: number, y: number, z: number): Vec3 {
  return Object.freeze({ __t: 'vec3' as const, x: fin(x), y: fin(y), z: fin(z) });
}

/** Construct an (immutable) list value — the `items` array is copied and frozen, so
 *  the caller's array can never later mutate a live value. Length is NOT checked
 *  here; `api.ts` owns the `SCR.LIST_MAX` ceiling (the one place it is enforced). */
export function list(items: readonly Value[]): ScriptList {
  return Object.freeze({ __t: 'list' as const, items: Object.freeze([...items]) });
}

export function isVec3(v: Value): v is Vec3 {
  return typeof v === 'object' && v.__t === 'vec3';
}

export function isList(v: Value): v is ScriptList {
  return typeof v === 'object' && v.__t === 'list';
}

export function valueType(v: Value): ValueType {
  if (typeof v === 'number') return 'number';
  if (typeof v === 'string') return 'string';
  if (typeof v === 'boolean') return 'bool';
  return v.__t === 'list' ? 'list' : 'vec3';
}

/** Structural equality for `==` / `!=`. Different types are never equal (no
 *  coercion → deterministic); vec3s compare component-wise; lists compare
 *  element-wise and recursively. A list is never equal to a vec3, and two
 *  separately-built lists with equal elements ARE equal (identity never matters). */
export function valuesEqual(a: Value, b: Value): boolean {
  if (isVec3(a) || isVec3(b)) return isVec3(a) && isVec3(b) && a.x === b.x && a.y === b.y && a.z === b.z;
  if (isList(a) || isList(b)) {
    if (!isList(a) || !isList(b) || a.items.length !== b.items.length) return false;
    for (let i = 0; i < a.items.length; i++) if (!valuesEqual(a.items[i]!, b.items[i]!)) return false;
    return true;
  }
  return a === b;
}

/** A stable, human-readable rendering used by `say` and error messages. Goldens are
 *  pinned in `tests/script_values.test.ts`: a vec3 is `(1, 2, 3)`, a list `[1, 2]`. */
export function valueToString(v: Value): string {
  if (isVec3(v)) return `(${num(v.x)}, ${num(v.y)}, ${num(v.z)})`;
  if (isList(v)) return `[${v.items.map((it) => valueToString(it)).join(', ')}]`;
  if (typeof v === 'number') return num(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return v;
}

function num(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 1000) / 1000);
}

function fin(n: number): number {
  return Number.isFinite(n) ? n : 0;
}
